/**
 * @module agent/adapters/services/AuthService
 *
 * JWT and Cognito authentication service.
 * Handles token management, user identity, and AWS SDK middleware.
 *
 * Responsibilities:
 * - JWT token retrieval and refresh
 * - User identity extraction
 * - AWS SDK middleware for JWT auth
 * - Payload building for AgentCore
 *
 * @example
 * const auth = new AuthService(authStore);
 * const token = await auth.getJwtToken();
 * const payload = auth.buildAgentPayload('Hello');
 */

import { getUserPool } from '../../../../api/cognito';

/**
 * Authentication service for JWT/Cognito management.
 */
class AuthService {
  /**
   * @param {Object|null} authStore - Auth store for user identity
   */
  constructor(authStore) {
    this._authStore = authStore;
    this._currentToken = null;
  }

  // ============================================================================
  // USER IDENTITY
  // ============================================================================

  /**
   * Get current user ID from auth store.
   * @returns {string|null} User ID or null
   */
  getUserId() {
    try {
      return (
        this._authStore?.currentUserId ||
        this._authStore?.userInfo?.id ||
        this._authStore?.user?.username ||
        null
      );
    } catch {
      return null;
    }
  }

  /**
   * Get current user email from auth store.
   * @returns {string|null} User email or null
   */
  getUserEmail() {
    try {
      // Try userInfo first
      if (this._authStore?.userInfo?.email) {
        return this._authStore.userInfo.email;
      }

      // Fallback to Cognito token
      if (this._authStore?.user?.idToken?.payload) {
        const payload = this._authStore.user.idToken.payload;
        return payload.email || payload['cognito:username'] || null;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Extract both user ID and email.
   * @param {string} [jwtToken] - Optional JWT token for fallback extraction
   * @returns {{ userId: string|null, userEmail: string|null }}
   */
  extractUserInfo(jwtToken = null) {
    let userId = this.getUserId();
    let userEmail = this.getUserEmail();

    // Fallback to JWT token parsing if no store data
    if ((!userId || !userEmail) && jwtToken) {
      const payload = this.parseJwtPayload(jwtToken);
      if (payload) {
        userId = userId || payload.sub || payload['cognito:username'];
        userEmail = userEmail || payload.email;
      }
    }

    return { userId: userId || null, userEmail: userEmail || null };
  }

  // ============================================================================
  // JWT TOKEN MANAGEMENT
  // ============================================================================

  /**
   * Get JWT token from Cognito session.
   * Uses ACCESS token (has client_id claim for AgentCore).
   * Automatically refreshes if token is expired or expiring soon.
   *
   * @returns {Promise<string|null>} JWT token or null
   */
  async getJwtToken() {
    return new Promise((resolve) => {
      try {
        const cognitoUser = getUserPool().getCurrentUser();

        if (!cognitoUser) {
          console.warn('[AuthService] No current Cognito user found');
          return resolve(null);
        }

        cognitoUser.getSession((err, session) => {
          if (err) {
            console.warn('[AuthService] Failed to get Cognito session:', err);
            return resolve(null);
          }

          const accessToken = session?.accessToken;
          const idToken = session?.idToken;

          // Check if access token needs refresh
          if (accessToken) {
            const expiryTime = accessToken.payload.exp * 1000;
            const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;

            if (expiryTime <= fiveMinutesFromNow) {
              // Token expired or expiring soon - refresh
              console.log('[AuthService] Token expiring soon, refreshing...');

              cognitoUser.refreshSession(session.refreshToken, (refreshErr, refreshedSession) => {
                if (refreshErr) {
                  console.error('[AuthService] Token refresh failed:', refreshErr);
                  return resolve(null);
                }

                const newAccessToken = refreshedSession?.accessToken?.jwtToken;
                const newIdToken = refreshedSession?.idToken?.jwtToken;

                console.log('[AuthService] Token refreshed successfully');

                if (newAccessToken) {
                  return resolve(newAccessToken);
                } else if (newIdToken) {
                  return resolve(newIdToken);
                } else {
                  return resolve(null);
                }
              });
            } else {
              // Token is valid
              return resolve(accessToken.jwtToken);
            }
          } else if (idToken) {
            // Fallback to ID token
            console.warn('[AuthService] Falling back to ID token');
            return resolve(idToken.jwtToken);
          } else {
            console.warn('[AuthService] No tokens found in session');
            return resolve(null);
          }
        });
      } catch (error) {
        console.error('[AuthService] Error getting JWT token:', error);
        resolve(null);
      }
    });
  }

  /**
   * Set current token for middleware access.
   * @param {string|null} token - JWT token
   */
  setCurrentToken(token) {
    this._currentToken = token;
  }

  /**
   * Get current token.
   * @returns {string|null} Current JWT token
   */
  getCurrentToken() {
    return this._currentToken;
  }

  /**
   * Parse JWT payload from token.
   * @param {string|null} token - JWT token
   * @returns {Object|null} Parsed payload or null
   */
  parseJwtPayload(token) {
    if (!token || typeof token !== 'string') {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      return JSON.parse(atob(parts[1]));
    } catch {
      return null;
    }
  }

  // ============================================================================
  // AWS SDK MIDDLEWARE
  // ============================================================================

  /**
   * Create JWT authentication middleware for AWS SDK.
   * Overrides AWS signature auth with Bearer token.
   *
   * @returns {{ middleware: Function, options: Object }}
   */
  createJwtMiddleware() {
    const self = this;

    return {
      middleware: (next) => async (args) => {
        const jwtToken = self._currentToken;

        if (!jwtToken) {
          console.warn('[AuthService] No JWT token available in middleware');
          return next(args);
        }

        // Extract user ID for optional header
        let userId = null;
        const payload = self.parseJwtPayload(jwtToken);
        if (payload) {
          userId = payload.sub || payload.username || payload['cognito:username'];
        }

        // Set JWT Bearer token
        args.request.headers = {
          ...args.request.headers,
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        };

        // Add user ID header if available
        if (userId) {
          args.request.headers['X-Amzn-Bedrock-AgentCore-Runtime-User-Id'] = userId;
        }

        // Remove AWS-specific headers that conflict with JWT
        delete args.request.headers['x-amz-date'];
        delete args.request.headers['x-amz-content-sha256'];
        delete args.request.headers['x-amz-security-token'];
        delete args.request.headers['x-amz-target'];
        delete args.request.headers['authorization']; // Remove lowercase version

        return next(args);
      },
      options: {
        step: 'finalizeRequest',
        name: 'jwtAuthMiddleware',
        priority: 'high',
        override: true
      }
    };
  }

  // ============================================================================
  // PAYLOAD BUILDING
  // ============================================================================

  /**
   * Build payload for AgentCore invocation.
   *
   * @param {string} content - User message content
   * @returns {Uint8Array} Encoded payload
   */
  buildAgentPayload(content) {
    const { userId, userEmail } = this.extractUserInfo();

    const payloadObject = {
      prompt: content,
      user: {
        cognitoId: userId,
        email: userEmail
      }
    };

    return new TextEncoder().encode(JSON.stringify(payloadObject));
  }
}

export default AuthService;
